import { useState } from "react";
import { motion } from "framer-motion";
import { C } from "../lib/colors";
import { STRIPE_LINKS, hasAnnualLink, isValidStripeUrl } from "../lib/stripe";
import { TIERS as SHARED_TIERS } from "../lib/tiers";
import { PageShell } from "../components/PageShell";

// Scroll-reveal config. `once: true` so animations don't replay every
// time the user scrolls past; `-60px` margin starts the motion just
// before the section enters view so it feels settled when read.
const inViewProps = {
  initial:    { opacity: 0, y: 28 },
  whileInView:{ opacity: 1, y: 0 },
  viewport:   { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const staggerParent = {
  initial:    "hidden",
  whileInView:"visible",
  viewport:   { once: true, margin: "-60px" },
  variants:   { visible: { transition: { staggerChildren: 0.07 } } },
};
const staggerChild = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

// Page-specific copy (turnaround + feature lists + which tier is "featured"
// in the comparison). Prices come from src/lib/tiers.js — never re-declare
// them here, or /pricing and /onboarding will drift apart.
const TIER_COPY = {
  starter: {
    turnaround: "5-day turnaround",
    features: [
      "4 SEO Blog Posts/mo",
      "8 Social Captions",
      "Content Strategy",
      "Client Portal",
      "Monthly Report",
      "5-day turnaround",
    ],
    featured: false,
  },
  growth: {
    turnaround: "3-day turnaround",
    features: [
      "9 SEO Blog Posts/mo",
      "21 Social Captions",
      "Full Content Strategy",
      "Weekly Reports",
      "1 Email Newsletter",
      "3-day turnaround",
    ],
    featured: true,
  },
  scale: {
    turnaround: "48-hour priority",
    features: [
      "18 SEO Blog Posts/mo",
      "42 Social Captions",
      "Advanced Strategy + Rank Tracking",
      "4 Email Newsletters",
      "Priority Support",
      "48-hour priority turnaround",
    ],
    featured: false,
  },
};

// Merge shared prices with page-local copy. `annual` is the
// monthly-equivalent display value; `annualTotal` is the actual yearly charge.
const TIERS = ["starter", "growth", "scale"].map(id => ({
  ...SHARED_TIERS[id],
  annual: SHARED_TIERS[id].annualMonthly,  // alias for legacy JSX below
  ...TIER_COPY[id],
}));

const TRIAL = {
  price:      SHARED_TIERS.trial.setupFee,
  days:       SHARED_TIERS.trial.days,
  convertsTo: SHARED_TIERS.trial.convertsTo,
  link:       STRIPE_LINKS.trial,
};

const FAQ = [
  ["Can I switch plans later?",
   "Yes — upgrade or downgrade anytime. Changes take effect on your next billing cycle. No fees."],
  ["What happens when I cancel?",
   "You keep access through the end of your current billing period — your card won't be charged again. We don't refund the time already paid (you keep using it through cancellation), and we don't charge a cancellation fee either. Just walk away clean at your renewal date."],
  ["Example: if I pay on the 5th and cancel on the 10th, what happens?",
   "You keep access through the 4th of the following month (the rest of the period you paid for). No further charge, no refund of the partial month — same answer regardless of when in the cycle you cancel."],
  ["Are there any setup fees?",
   "No. The price you see is the price you pay. Onboarding, strategy, and your client portal are all included."],
  ["How does the 15-day trial work?",
   "Pay $97 today and we run your content for 15 days. If you don't cancel before day 15, you roll into the Starter plan at $397/mo. Cancel anytime during the trial and you're out clean — no further charge."],
  ["Annual vs monthly — what's the difference?",
   "Same content, same service. Monthly is billed each month; annual is a single yearly charge (no monthly installments inside an annual plan). Annual saves up to ~16% depending on your tier — Scale saves the most."],
  ["What if I need more content than Scale offers?",
   "Reach out and we'll quote a custom plan. Most clients fit cleanly into one of the three tiers."],
];

// Feature matrix used by the comparison chart at the bottom of the page.
// "trial" column shows what the 15-day window unlocks (proportional sample),
// then Starter / Growth / Scale follow with their full-month entitlements.
const FEATURE_MATRIX = [
  { row: "SEO Blog Posts",        trial: "2 (sample)",   starter: "4 / mo",  growth: "9 / mo",   scale: "18 / mo" },
  { row: "Social Captions",       trial: "4 (sample)",   starter: "8 / mo",  growth: "21 / mo",  scale: "42 / mo" },
  { row: "Email Newsletters",     trial: "—",            starter: "—",       growth: "1 / mo",   scale: "4 / mo" },
  { row: "Content Strategy",      trial: "Basic brief",  starter: "Basic",   growth: "Full",     scale: "Advanced" },
  { row: "Reporting",             trial: "—",            starter: "Monthly", growth: "Weekly",   scale: "Weekly + rank tracking" },
  { row: "Turnaround",            trial: "5-day",        starter: "5-day",   growth: "3-day",    scale: "48-hour" },
  { row: "Client Portal",         trial: true,           starter: true,      growth: true,       scale: true },
  { row: "Approve / Request edits", trial: true,         starter: true,      growth: true,       scale: true },
  { row: "Priority Support",      trial: false,          starter: false,     growth: false,      scale: true },
  { row: "Cancel anytime",        trial: true,           starter: true,      growth: true,       scale: true },
  { row: "Keep paid period after cancel", trial: true,   starter: true,      growth: true,       scale: true },
];

const CycleToggle = ({ cycle, setCycle }) => (
  <div role="radiogroup" aria-label="Billing cycle"
    style={{
      display: "inline-flex", padding: 4, borderRadius: 11,
      background: C.s1, border: `1px solid ${C.border}`,
      marginBottom: 32, gap: 4,
    }}>
    {[
      { id: "monthly", label: "Monthly" },
      { id: "annual",  label: "Annual",  badge: "Save up to 16%" },
    ].map((opt) => {
      const active = cycle === opt.id;
      return (
        <button key={opt.id} role="radio" aria-checked={active}
          onClick={() => setCycle(opt.id)}
          style={{
            background: active ? C.green : "transparent",
            color: active ? "#000" : C.muted,
            border: "none", borderRadius: 8,
            padding: "8px 18px", fontSize: 13, fontWeight: 700,
            cursor: "pointer", fontFamily: "'Instrument Sans',sans-serif",
            transition: "all 0.18s",
            display: "inline-flex", alignItems: "center", gap: 8,
          }}>
          {opt.label}
          {opt.badge && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
              background: active ? "rgba(0,0,0,0.12)" : `${C.green}20`,
              color: active ? "#000" : C.green,
              padding: "2px 7px", borderRadius: 5,
              fontFamily: "'DM Mono',monospace",
            }}>
              {opt.badge}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// Renders a single matrix cell. Booleans get ✓ / × icons; strings render as-is.
const MatrixCell = ({ value, featured }) => {
  if (value === true) {
    return (
      <span aria-label="Included" style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        width:22, height:22, borderRadius:"50%",
        background: featured ? `${C.green}25` : "rgba(255,255,255,0.06)",
        color: featured ? C.green : "#fff",
        fontSize:12, fontWeight:800,
      }}>✓</span>
    );
  }
  if (value === false) {
    return (
      <span aria-label="Not included" style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        width:22, height:22, borderRadius:"50%",
        background:"rgba(255,255,255,0.03)",
        color:"rgba(255,255,255,0.25)",
        fontSize:12, fontWeight:700,
      }}>×</span>
    );
  }
  return (
    <span style={{
      fontSize:12, color: featured ? "#fff" : C.text, fontFamily:"'Instrument Sans',sans-serif",
      fontWeight: featured ? 600 : 500,
    }}>
      {value === "—" ? <span style={{color:"rgba(255,255,255,0.25)"}}>—</span> : value}
    </span>
  );
};

// sessionStorage key used to hand the chosen tier + cycle off to
// OnboardingPage. Lives only for the tab — refreshing /onboarding after
// the form is restored from this key would otherwise re-apply the
// Pricing-page selection on every page load. OnboardingPage clears it
// as soon as it reads it.
const PRICING_PRESELECT_KEY = "moap.pricing.preselect.v1";

export const PricingPage = ({ onNavigate, authRole }) => {
  const [cycle, setCycle] = useState("monthly");
  const trialAvailable = isValidStripeUrl(TRIAL.link);
  // The "keep your paid X" copy adapts to whichever cycle the toggle is on,
  // so an annual subscriber doesn't see "month" in the cancel-policy chip.
  const periodWord = cycle === "annual" ? "year" : "month";

  // CTA handler shared by every tier card + the trial CTA. Stashes the
  // chosen tier + cycle in sessionStorage and sends the visitor to the
  // onboarding form. We deliberately don't link to Stripe directly:
  // without a pending_onboardings row our webhook can't bind the
  // resulting checkout.session.completed event to a real customer, and
  // before this routing change anyone who paid from /pricing landed in
  // the orphan branch (no invite, owner alert email only). Forcing the
  // form first means every payment has the data the agent pipeline needs.
  const goToOnboardingWith = (tier) => {
    try {
      window.sessionStorage.setItem(
        PRICING_PRESELECT_KEY,
        JSON.stringify({ tier, cycle: tier === "trial" ? "monthly" : cycle, at: Date.now() }),
      );
    } catch { /* quota / private-mode — proceed; form just won't be pre-filled */ }
    onNavigate("onboarding");
  };

  return (
    <PageShell onNavigate={onNavigate} authRole={authRole}>
      {/* 1 — Header — fades in on mount (first-paint section) */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        style={{maxWidth:760,margin:"0 auto",padding:"64px 24px 8px",textAlign:"center"}}>
        <div style={{fontSize:11,fontWeight:700,letterSpacing:2.5,textTransform:"uppercase",
          color:C.green,marginBottom:14,fontFamily:"'DM Mono',monospace"}}>Pricing</div>
        <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(32px,5vw,52px)",fontWeight:800,
          letterSpacing:-2,marginBottom:14}}>Simple, transparent pricing.</h1>
        <p style={{color:C.muted,fontSize:16,maxWidth:520,margin:"0 auto 0",lineHeight:1.6}}>
          No hidden fees. No long-term contracts. Cancel anytime — you keep your paid {periodWord}.
        </p>
      </motion.div>

      {/* 2 — How we stack up — heading fades in, cards stagger */}
      <div style={{padding:"56px 5% 16px"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <motion.div {...inViewProps}>
            <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(22px,3vw,30px)",
              fontWeight:800,letterSpacing:-1,marginBottom:14,textAlign:"center"}}>How we stack up</h2>
            <p style={{color:C.muted,fontSize:14,maxWidth:560,margin:"0 auto 32px",
              lineHeight:1.6,textAlign:"center"}}>
              Same problem, five very different solutions. Here's the trade-off at every price point.
            </p>
          </motion.div>
          <motion.div {...staggerParent}
            style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
            {[
              { label:"DIY AI tool",            example:"e.g. Jasper, Copy.ai", price:"$36–$69/mo",
                note:"You write the prompts, edit every draft, and post it yourself. Cheapest if you have the time and skill.",
                color:C.muted, featured:false },
              { label:"Productized subscription", example:"e.g. Feedbird, Verblio", price:"$99–$1,500/mo",
                note:"Order content a la carte. No bundled strategy or reports. You pick the topics.",
                color:C.muted, featured:false },
              { label:"Media on Auto Pilot",     example:"You're here", price:"From $397/mo",
                note:"AI-powered, fully automated content + strategy + reports + portal. Bundled. Cancel anytime.",
                color:C.green, featured:true },
              { label:"Freelancer",              example:"Upwork, Contra", price:"$1,500/mo",
                note:"Variable quality, single point of failure, no strategy or reporting.",
                color:C.amber, featured:false },
              { label:"Traditional agency",      example:"Mid-market firms", price:"$3,000–$15,000/mo",
                note:"Full strategic partner. Slower turnaround, long contracts, often gated behind quotes.",
                color:C.muted, featured:false },
            ].map((row,i)=>(
              <motion.div key={i} variants={staggerChild}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 280, damping: 22 }}
                style={{
                  background: row.featured ? "linear-gradient(160deg,#0F1A14,#0D1510)" : C.s2,
                  border:`1px solid ${row.featured ? `${C.green}40` : C.border}`,
                  borderRadius:14, padding:"20px 18px",
                  borderTop:`3px solid ${row.color}`,
                  position:"relative",
                  willChange:"transform",
                }}>
                {row.featured && (
                  <div style={{position:"absolute",top:-10,right:14,
                    background:C.green,color:"#000",padding:"2px 9px",borderRadius:100,
                    fontSize:9,fontWeight:800,letterSpacing:0.8,textTransform:"uppercase",
                    fontFamily:"'DM Mono',monospace"}}>
                    Us
                  </div>
                )}
                <div style={{fontSize:11,color: row.featured ? C.green : C.muted,
                  textTransform:"uppercase",letterSpacing:1.4,
                  fontFamily:"'DM Mono',monospace",marginBottom:3,fontWeight:700}}>
                  {row.label}
                </div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",
                  fontFamily:"'DM Mono',monospace",marginBottom:10}}>
                  {row.example}
                </div>
                <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,
                  color: row.featured ? C.green : row.color,
                  marginBottom:8,lineHeight:1.1,letterSpacing:-0.5}}>
                  {row.price}
                </div>
                <div style={{fontSize:12,color: row.featured ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.55)",
                  lineHeight:1.55}}>
                  {row.note}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* 3 — Tiers (toggle + trial CTA + cards) */}
      <div style={{maxWidth:1000,margin:"0 auto",padding:"40px 24px 0",textAlign:"center"}}>
        <CycleToggle cycle={cycle} setCycle={setCycle}/>

        {/* Trust strip — factual, not testimonial. */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:18,
          marginBottom:28,flexWrap:"wrap",fontSize:11,color:"rgba(255,255,255,0.45)",
          fontFamily:"'DM Mono',monospace"}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
            <span style={{color:C.green}}>✓</span> First content within 48 hours of onboarding
          </span>
          <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
            <span style={{color:C.green}}>✓</span> Cancel anytime, no further charges
          </span>
          <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
            <span style={{color:C.green}}>✓</span> Keep your paid {periodWord}
          </span>
        </div>

        {/* Tier cards — stagger reveal + tactile hover lift */}
        <motion.div {...staggerParent}
          style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",
            gap:20,marginBottom:8}}>
          {TIERS.map((p) => {
            // In annual mode the big number IS the yearly charge. Customers
            // pay once a year — there are no monthly installments inside the
            // annual subscription — so showing a per-month equivalent would
            // misrepresent the billing model.
            const price = cycle === "annual" ? p.annualTotal : p.monthly;
            const savings = (p.monthly * 12) - p.annualTotal;
            const annualConfigured = hasAnnualLink(p.id);
            const showAnnualPendingNote = cycle === "annual" && !annualConfigured;
            return (
              <motion.div key={p.id} variants={staggerChild}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 280, damping: 22 }}
                style={{
                  background: p.featured ? "linear-gradient(160deg,#0F1A14,#0D1510)" : C.s1,
                  border: `1px solid ${p.featured ? "rgba(0,240,160,0.35)" : C.border}`,
                  borderRadius: 20, padding: "36px 28px",
                  position: "relative",
                  display: "flex", flexDirection: "column", textAlign: "left",
                  willChange: "transform",
                }}>
                {p.featured && (
                  <div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",
                    background:C.green,color:"#000",padding:"3px 16px",borderRadius:100,
                    fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
                    whiteSpace:"nowrap"}}>Most Popular</div>
                )}
                <div style={{fontSize:12,fontWeight:700,letterSpacing:2,textTransform:"uppercase",
                  color:C.muted,marginBottom:12,fontFamily:"'DM Mono',monospace"}}>{p.name}</div>
                <div style={{fontFamily:"'Sora',sans-serif",fontSize:52,fontWeight:800,
                  lineHeight:1,letterSpacing:-2,marginBottom:4,
                  color:p.featured?C.green:"#fff"}}>
                  <span style={{fontSize:18,fontWeight:500,color:C.muted,
                    verticalAlign:"super",fontFamily:"'Instrument Sans',sans-serif",letterSpacing:0}}>$</span>
                  {price.toLocaleString()}
                </div>
                <div style={{fontSize:13,color:C.muted,marginBottom:4}}>
                  {cycle === "annual" ? (
                    <>per year, billed once</>
                  ) : (
                    <>per month, billed monthly</>
                  )}
                </div>
                {cycle === "annual" && (
                  <div style={{fontSize:11,color:C.green,marginBottom:8,fontFamily:"'DM Mono',monospace",
                    fontWeight:700}}>
                    Save ${savings.toLocaleString()} vs monthly
                  </div>
                )}
                <div style={{fontSize:11,color:"rgba(255,255,255,0.2)",marginBottom:28,
                  fontFamily:"'DM Mono',monospace"}}>Secured by Stripe</div>
                {/* Per-tier feature bullets removed — full breakdown is in
                    the comparison matrix below. Keeps cards lean + the
                    price as the focal point. */}
                <div style={{flex:1}}/>

                {showAnnualPendingNote && (
                  <div style={{fontSize:11,color:C.amber,marginBottom:10,lineHeight:1.5,
                    background:`${C.amber}10`,border:`1px solid ${C.amber}30`,
                    borderRadius:8,padding:"7px 10px"}}>
                    Annual billing for this tier is being set up. Email us to lock in the rate
                    — meanwhile the checkout below is monthly.
                  </div>
                )}

                <button type="button" onClick={() => goToOnboardingWith(p.id)}
                  style={{
                    display:"flex",alignItems:"center",justifyContent:"center",gap:9,
                    width:"100%",padding:13,borderRadius:10,fontWeight:700,fontSize:14,
                    cursor:"pointer",transition:"all 0.2s",fontFamily:"'Instrument Sans',sans-serif",
                    marginBottom:10,
                    background:p.featured?C.green:"#fff",color:"#000",border:"none",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = p.featured ? "#7CFFCD" : "#E8ECF4"}
                  onMouseLeave={(e) => e.currentTarget.style.background = p.featured ? C.green : "#fff"}>
                  Get started with {p.name} →
                </button>
                <div style={{textAlign:"center",fontSize:10,color:"rgba(255,255,255,0.35)",
                  marginBottom:6,fontFamily:"'DM Mono',monospace",letterSpacing:0.5}}>
                  A few quick questions, then secure checkout via Stripe
                </div>
                <div style={{textAlign:"center",fontSize:11,color:"rgba(255,255,255,0.3)",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                  <span>🛡</span> Cancel anytime · Keep your paid {periodWord}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Trial CTA — below the tier cards as a lower-friction alternative
            for prospects who scrolled the tiers and weren't sold. Pay $97
            today, get 15 days, auto-convert to Starter on day 16 unless
            cancelled. See docs/stripe-trial-setup.md. */}
        <div style={{
          background:"linear-gradient(135deg,rgba(77,170,255,0.08),rgba(0,240,160,0.05))",
          border:`1px solid ${C.blue}40`,
          borderRadius:14, padding:"18px 22px", marginTop:36,
          display:"flex",alignItems:"center",justifyContent:"space-between",
          gap:18, flexWrap:"wrap",textAlign:"left",maxWidth:760,marginLeft:"auto",marginRight:"auto",
        }}>
          <div style={{flex:1,minWidth:240}}>
            <div style={{fontSize:10,color:C.blue,fontFamily:"'DM Mono',monospace",
              textTransform:"uppercase",letterSpacing:1.5,marginBottom:5,fontWeight:700}}>
              Not ready to commit?
            </div>
            <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,color:"#fff",
              marginBottom:5,letterSpacing:-0.4}}>
              {TRIAL.days}-day trial for ${TRIAL.price}
            </div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.55}}>
              Auto-converts to Starter (${TRIAL.convertsTo}/mo) on day {TRIAL.days + 1} if you don't cancel.
              Cancel anytime during the trial — no charge beyond ${TRIAL.price}.
            </div>
          </div>
          {trialAvailable ? (
            <button type="button" onClick={() => goToOnboardingWith("trial")}
              style={{background:C.blue,color:"#000",border:"none",borderRadius:10,
                padding:"12px 22px",fontSize:13,fontWeight:700,cursor:"pointer",
                fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>
              Start trial →
            </button>
          ) : (
            <div style={{fontSize:11,color:C.amber,fontFamily:"'DM Mono',monospace",
              background:`${C.amber}10`,border:`1px solid ${C.amber}30`,
              borderRadius:9,padding:"9px 13px",lineHeight:1.5,maxWidth:280}}>
              Trial checkout coming soon — see docs/stripe-trial-setup.md
            </div>
          )}
        </div>
      </div>

      {/* 4 — Pricing comparison chart (feature matrix) */}
      <div style={{maxWidth:1100,margin:"56px auto 0",padding:"0 5%"}}>
        <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(22px,3vw,30px)",fontWeight:800,
          letterSpacing:-1,marginBottom:8,textAlign:"center"}}>Compare features</h2>
        <p style={{color:C.muted,fontSize:14,maxWidth:560,margin:"0 auto 32px",
          lineHeight:1.6,textAlign:"center"}}>
          The full breakdown of what's in each plan — including what the trial unlocks.
        </p>
        <div style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:16,
          overflow:"hidden"}}>
          {/* The matrix itself — horizontal scroll on narrow viewports so the
              column headers never collapse on phones. */}
          <div style={{overflowX:"auto"}}>
            <table style={{
              width:"100%", minWidth:680,
              borderCollapse:"collapse",
              fontFamily:"'Instrument Sans',sans-serif",
            }}>
              <thead>
                <tr style={{background:"rgba(255,255,255,0.02)",
                  borderBottom:`1px solid ${C.border}`}}>
                  <th style={{textAlign:"left",padding:"18px 22px",fontSize:11,
                    fontFamily:"'DM Mono',monospace",fontWeight:700,
                    color:C.muted,textTransform:"uppercase",letterSpacing:1.5,
                    minWidth:200}}>
                    Feature
                  </th>
                  {[
                    { name:"Trial",   price:"$97",    sub:"15 days",   color:C.blue,   featured:false },
                    { name:"Starter", price:"$397",   sub:"per month", color:"#fff",   featured:false },
                    { name:"Growth",  price:"$797",   sub:"per month", color:C.green,  featured:true  },
                    { name:"Scale",   price:"$1,497", sub:"per month", color:"#fff",   featured:false },
                  ].map((col) => (
                    <th key={col.name} style={{textAlign:"center",padding:"14px 14px",
                      borderLeft:`1px solid ${C.border}`,
                      background: col.featured ? `${C.green}08` : "transparent",
                      minWidth:140}}>
                      <div style={{fontSize:10,color: col.featured ? C.green : C.muted,
                        fontFamily:"'DM Mono',monospace",textTransform:"uppercase",
                        letterSpacing:1.4,fontWeight:700,marginBottom:6}}>
                        {col.name}
                        {col.featured && <span style={{marginLeft:5,color:C.green}}>★</span>}
                      </div>
                      <div style={{fontFamily:"'Sora',sans-serif",fontSize:20,fontWeight:800,
                        color: col.color,lineHeight:1,marginBottom:3,letterSpacing:-0.5}}>
                        {col.price}
                      </div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",
                        fontFamily:"'DM Mono',monospace"}}>{col.sub}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_MATRIX.map((r, i) => (
                  <tr key={r.row} style={{
                    background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                    borderTop:`1px solid ${C.border}`,
                  }}>
                    <td style={{padding:"12px 22px",fontSize:13,fontWeight:600,color:"#fff"}}>
                      {r.row}
                    </td>
                    <td style={{textAlign:"center",padding:"12px 14px",
                      borderLeft:`1px solid ${C.border}`}}>
                      <MatrixCell value={r.trial}/>
                    </td>
                    <td style={{textAlign:"center",padding:"12px 14px",
                      borderLeft:`1px solid ${C.border}`}}>
                      <MatrixCell value={r.starter}/>
                    </td>
                    <td style={{textAlign:"center",padding:"12px 14px",
                      borderLeft:`1px solid ${C.border}`,
                      background:`${C.green}08`}}>
                      <MatrixCell value={r.growth} featured/>
                    </td>
                    <td style={{textAlign:"center",padding:"12px 14px",
                      borderLeft:`1px solid ${C.border}`}}>
                      <MatrixCell value={r.scale}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Cancel-policy footnote — clarifies the no-refund-but-keep-period
              model. Lives inside the same chart card so it's read in context. */}
          <div style={{borderTop:`1px solid ${C.border}`,padding:"14px 22px",
            background:"rgba(255,255,255,0.02)",
            fontSize:12,color:C.muted,lineHeight:1.6}}>
            <span style={{color:C.green,marginRight:6,fontWeight:700}}>ℹ</span>
            <strong style={{color:"#fff"}}>How cancellation works:</strong>{" "}
            Cancel anytime to stop the next billing cycle. You keep full access
            through the end of the period you've already paid for (month or
            year, depending on your plan) — no refund of the partial period
            (since you're using it through cancellation), and no cancellation
            fee either.
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div style={{maxWidth:680,margin:"0 auto",padding:"56px 24px"}}>
        <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(22px,3vw,30px)",fontWeight:800,
          letterSpacing:-1,marginBottom:32,textAlign:"center"}}>Pricing FAQ</h2>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {FAQ.map(([q,a],i)=>(
            <div key={i} style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:12,padding:"18px 22px"}}>
              <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:8}}>{q}</div>
              <div style={{fontSize:13,color:C.muted,lineHeight:1.7}}>{a}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{padding:"0 5% 56px"}}>
        <div style={{background:"linear-gradient(135deg,rgba(0,240,160,0.07),rgba(77,170,255,0.05))",
          border:`1px solid rgba(0,240,160,0.18)`,borderRadius:22,padding:"56px 40px",
          textAlign:"center",maxWidth:720,margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(22px,3.5vw,36px)",fontWeight:800,
            letterSpacing:-1,marginBottom:12}}>
            Still have questions?
          </h2>
          <p style={{color:C.muted,fontSize:15,maxWidth:420,margin:"0 auto 28px",lineHeight:1.6}}>
            We're happy to help you pick the right plan for your business — no pressure, no sales pitch.
          </p>
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={()=>onNavigate("contact")} style={{background:C.green,color:"#000",border:"none",borderRadius:10,
              padding:"12px 26px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif"}}>
              Talk to Us →
            </button>
            <button onClick={()=>onNavigate("onboarding")} style={{background:"transparent",color:C.text,border:`1px solid ${C.border}`,borderRadius:10,
              padding:"12px 26px",fontSize:14,fontWeight:500,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif"}}>
              Get Started
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
};
